import api from 'services/api';
import { REJECT, array, bool, nullable, num, object, oneOf, optional, str, type Received } from 'validation';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { readResponse } from 'api/shared/readResponse';

import {
  TRIGGER_ACTIONS,
  TRIGGER_RESTART_FIRE_MODE_DEFAULT,
  TRIGGER_RESTART_FIRE_MODES,
  triggerParameterSchema,
  triggerStatusField,
  type TriggerAction,
  type TriggerParameter,
  type TriggerRestartFireMode,
  type TriggerStatusList,
} from './types';

/**
 * The request body for both `POST /triggers` (create) and `PUT /triggers/:id`
 * (update). **Permissive on purpose** — the server is the validation authority
 * (name uniqueness per zone, the action↔target pairing, monitor-zone privileges,
 * folder-in-zone). A too-strict client type would block valid sends when backend
 * rules loosen; the FE authors this body, so there is no response to validate.
 *
 * Shared by POST and PUT because the FE sends the same shape to both.
 *
 * PERMISSIVE ON VALUES, STRICT ON PRESENCE. Two fields on the update path mean
 * something different when the KEY IS MISSING than when the value is null/empty,
 * because the route branches on `hasOwnProperty`:
 *
 * - `monitor_zone_ids` — an absent key leaves the existing monitor set untouched;
 *   an EMPTY ARRAY means "monitor all zones, present and future". Two opposite
 *   outcomes, so a projection that forgot the field would silently preserve a stale
 *   scope rather than applying the user's choice.
 * - `folder_id` — an absent key leaves the folder untouched; an explicit `null`
 *   clears it.
 *
 * So neither is declared `?:`. Every field here is required-presence with a
 * permissive value: a construction site that omits one is a compile error, while
 * any value the user can produce still goes out. See the "Exhaustive field
 * forwarding" rule — `field: T | undefined` over `field?: T` — applied here in its
 * strongest form, since each field always has an answer.
 *
 * `documentation` (migration 0288) is absent on purpose, and absence is the safe
 * choice on BOTH routes: the PUT gates the column on `documentationProvided`, so
 * omitting the key preserves what is stored, and a freshly created trigger has
 * nothing to preserve. #1047 owns the column's UI across all thirteen tables it was
 * added to. See the note on `getTrigger`, the read that does return it.
 */
export interface SaveTrigger {
  readonly name: string | null;
  readonly zone_id: number | null;
  readonly type: string;
  readonly folder_id: number | null;
  // `[]` is not "none" — it is the wire encoding of monitor-all-zones.
  readonly monitor_zone_ids: readonly number[];
  readonly node_definition_id: number | null;
  // Null is any status. A non-empty list matches any of those statuses.
  // An empty array is rejected by the API; the editor sends null instead.
  readonly from_status: TriggerStatusList;
  readonly to_status: TriggerStatusList;
  readonly restart_fire_mode: TriggerRestartFireMode;
  readonly action: TriggerAction;
  readonly target_node_definition_id: number | null;
  readonly target_event_id: number | null;
  readonly queue_id: number | null;
  readonly parameters: readonly TriggerParameter[];
  // Only meaningful for `submit_child`. Send null, never `''`: the route validates any non-null value as an IANA name.
  readonly timezone: string | null;
}

// POST /triggers — create. Backend: routes/triggers.js inserts the trigger and its
// status-change row, then re-reads `SELECT t.*, z.name AS zone_name` and attaches
// `folder_path`, the `status_change` sub-record and the monitor zones.
//
// `CreatedTrigger` and its nested `CreatedTriggerStatusChange` are their OWN types
// + schemas, even though they overlap the read shapes: they are produced by
// distinct backend statements — a third verbatim copy of the same
// `SELECT tnsc.*, …` — which is equal-by-coincidence, not identity (see
// ../../README.md). Structural typing lets the result flow wherever a `Trigger` is
// expected downstream, which is what the edit model's reinitialize relies on.

export interface CreatedTriggerStatusChange {
  readonly trigger_node_status_change_id: number;
  readonly trigger_id: number;
  readonly node_definition_id: number | null;
  readonly node_definition_name: string | null;
  readonly from_status: TriggerStatusList;
  readonly to_status: TriggerStatusList;
  readonly action: TriggerAction;
  readonly target_node_definition_id: number | null;
  readonly target_node_definition_name: string | null;
  readonly target_default_queue_id: number | null;
  readonly target_event_id: number | null;
  readonly target_event_name: string | null;
  readonly target_event_zone_name: string | null;
  readonly queue_id: number | null;
  readonly queue_name: string | null;
  readonly queue_zone_name: string | null;
  readonly parameters: readonly TriggerParameter[] | null;
  readonly restart_fire_mode: TriggerRestartFireMode;
  readonly timezone: string | null;
  // Nullable for the same reason as on the reads: the columns carry a default but
  // no NOT NULL. See getTriggers.
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

export interface CreatedTrigger {
  readonly trigger_id: number;
  readonly zone_id: number | null;
  readonly zone_name: string | null;
  readonly name: string;
  readonly type: string;
  readonly folder_id: number | null;
  readonly folder_path: FolderPath | null;
  readonly monitor_all_zones: boolean;
  readonly monitor_zone_ids: readonly number[];
  readonly monitor_zone_names: readonly string[];
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly status_change?: CreatedTriggerStatusChange;
}

const createdStatusChangeSchema = object<CreatedTriggerStatusChange>({
  trigger_node_status_change_id: num(),
  trigger_id: num(),
  node_definition_id: nullable(num()),
  node_definition_name: nullable(str()),
  from_status: triggerStatusField(),
  to_status: triggerStatusField(),
  action: oneOf(TRIGGER_ACTIONS, 'raise_event'),
  target_node_definition_id: nullable(num()),
  target_node_definition_name: nullable(str()),
  target_default_queue_id: nullable(num()),
  target_event_id: nullable(num()),
  target_event_name: nullable(str()),
  target_event_zone_name: nullable(str()),
  queue_id: nullable(num()),
  queue_name: nullable(str()),
  queue_zone_name: nullable(str()),
  parameters: nullable(array(triggerParameterSchema)),
  restart_fire_mode: oneOf(TRIGGER_RESTART_FIRE_MODES, TRIGGER_RESTART_FIRE_MODE_DEFAULT),
  timezone: nullable(str()),
  created_at: nullable(str()),
  updated_at: nullable(str()),
});

const createdSchema = object<CreatedTrigger>({
  trigger_id: num(REJECT),
  zone_id: nullable(num()),
  zone_name: nullable(str()),
  name: str(),
  type: str(),
  folder_id: nullable(num()),
  folder_path: nullable(folderPathSchema),
  monitor_all_zones: bool(),
  monitor_zone_ids: array(num()),
  monitor_zone_names: array(str()),
  created_at: nullable(str()),
  updated_at: nullable(str()),
  status_change: optional(createdStatusChangeSchema),
});

/**
 * POST `/triggers`. Returns the created record.
 *
 * Rejects on HTTP error — the trigger was not created, and the server's complaint
 * (the 400 the route raises for a duplicate name in the zone, a missing
 * action target, or a monitor zone the user may not use) is the useful thing to
 * report.
 *
 * Answers with the **degraded** or **unreadable** arm when the trigger WAS created
 * and its receipt cannot be read in full: the rows are committed by then, so that
 * is not a failed save and must not be reported as one — a retry would insert a
 * second trigger watching the same transition, and both would fire (#1035).
 *
 * Only `trigger_id` carries `REJECT` — the one field with no substitute for any
 * consumer. Nothing opens a form on this value: the caller keeps only whether the
 * answer was `ok` (as an out-of-sync flag) and refetches the list, so a drift
 * anywhere in the receipt says "saved, but the response could not be read" and
 * nothing more.
 */
export const postTrigger = async (save: SaveTrigger): Promise<Received<CreatedTrigger>> => {
  const res = await api.post('/triggers', save);
  return readResponse(res.data, { schema: createdSchema, source: 'postTrigger' });
};
